/**
 * GroupPage — 所有权集团概览
 *
 * 显示当前用户所属集团及旗下子公司列表，支持一键切换工作台。
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Network, Users, ArrowRight, RefreshCw, Shield, MapPin, Clock3, Coins, AlertTriangle, Activity, CircleOff, ListFilter, BarChart3, BriefcaseBusiness, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  groupApi,
  type TenantGroup,
  type GroupTenant,
  type GroupMember,
  type GroupCommercialSummaryResponse,
  type GroupCommercialTenantSummary,
  type GroupInterCompanyResponse,
  type GroupAuditResponse,
  type GroupAuditFinding,
  type GroupFinanceRollupResponse,
} from '@/lib/network-api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

// ─── entity_type display ────────────────────────────

const ENTITY_COLORS: Record<string, string> = {
  hq: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  subsidiary: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  branch: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  associate: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
};

function formatCountryName(countryCode?: string | null) {
  if (!countryCode) return null;
  try {
    return new Intl.DisplayNames(undefined, { type: 'region' }).of(countryCode.toUpperCase()) ?? countryCode;
  } catch {
    return countryCode;
  }
}

function formatTimezoneLabel(timezone?: string) {
  if (!timezone) return null;
  const city = timezone.split('/').pop()?.replace(/_/g, ' ');
  return city || timezone;
}

function formatOfficeBase(tenant: GroupTenant, t: (key: string, options?: Record<string, unknown>) => string) {
  const country = formatCountryName(tenant.country_code);
  const timezone = formatTimezoneLabel(tenant.timezone);
  if (country && timezone) return t('networkPage.groups.officeBaseValue', { country, city: timezone });
  if (country) return country;
  if (timezone) return timezone;
  return t('networkPage.groups.noOfficeBase');
}

function formatMoney(value: number, currency?: string | null) {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: currency || 'SGD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number.isFinite(value) ? value : 0);
}

function formatDate(value?: string | null) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-SG', { day: '2-digit', month: 'short' });
}

function commercialTone(status?: GroupCommercialTenantSummary['healthStatus']) {
  switch (status) {
    case 'attention':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    case 'active':
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    default:
      return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
  }
}

type CommercialFilter = 'all' | 'attention' | 'active' | 'unavailable';

function matchesCommercialFilter(
  commercial: GroupCommercialTenantSummary | undefined,
  filter: CommercialFilter,
) {
  if (filter === 'all') return true;
  if (!commercial) return false;
  if (filter === 'unavailable') return commercial.dataStatus === 'unavailable';
  return commercial.healthStatus === filter && commercial.dataStatus === 'ready';
}

function MetricCard({ label, value, icon: Icon }: { label: string; value: string; icon: LucideIcon }) {
  return (
    <div className="rounded-lg border bg-background px-3 py-2">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-1 font-medium text-foreground">{value}</div>
    </div>
  );
}

// ─── Subsidiary Card ─────────────────────────────────

function SubsidiaryCard({
  tenant,
  commercial,
  isCurrent,
  isFocused,
  onSwitch,
  onOpenPipeline,
  onOpenAccount360,
  switching,
}: {
  tenant: GroupTenant;
  commercial?: GroupCommercialTenantSummary;
  isCurrent: boolean;
  isFocused?: boolean;
  onSwitch: () => void;
  onOpenPipeline?: () => void;
  onOpenAccount360?: () => void;
  switching: boolean;
}) {
  const { t } = useTranslation();
  const entityLabels: Record<string, string> = {
    hq: t('networkPage.groupEntityTypes.hq'),
    subsidiary: t('networkPage.groupEntityTypes.subsidiary'),
    branch: t('networkPage.groupEntityTypes.branch'),
    associate: t('networkPage.groupEntityTypes.associate'),
  };
  const entityLabel = entityLabels[tenant.entity_type] ?? tenant.entity_type ?? t('networkPage.groupEntityTypes.default');
  const entityColor = ENTITY_COLORS[tenant.entity_type] ?? ENTITY_COLORS['subsidiary']!;
  const officeBase = formatOfficeBase(tenant, t);
  const timezoneLabel = formatTimezoneLabel(tenant.timezone);

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg border p-3 transition-all',
        isCurrent
          ? 'border-primary/50 bg-primary/5 shadow-sm'
          : 'border-border bg-card hover:border-primary/30 hover:shadow-sm',
        isFocused && 'ring-2 ring-amber-400/70 ring-offset-2 ring-offset-background',
      )}
    >
      {/* Icon */}
      <div className={cn(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
        isCurrent ? 'bg-primary/10' : 'bg-muted',
      )}>
        <Building2 className={cn('h-4 w-4', isCurrent ? 'text-primary' : 'text-muted-foreground')} />
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="truncate text-sm font-medium">{tenant.name}</span>
          {tenant.entity_type === 'hq' && (
            <Badge className="h-4 px-1.5 text-[10px] bg-purple-600 text-white">{t('networkPage.groups.hqLabel')}</Badge>
          )}
          {isCurrent && (
            <Badge variant="outline" className="h-4 px-1.5 text-[10px] border-primary/30 text-primary">{t('networkPage.groups.current')}</Badge>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-2 flex-wrap">
          <span className={cn('rounded-full px-2 py-0 text-[10px] font-medium', entityColor)}>
            {entityLabel}
          </span>
          {tenant.status && tenant.status !== 'active' && (
            <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">{tenant.status}</Badge>
          )}
        </div>
        <div className="mt-2 grid gap-1 text-[11px] text-muted-foreground sm:grid-cols-3">
          <span className="flex items-center gap-1.5">
            <MapPin className="h-3 w-3" />
            {officeBase}
          </span>
          {timezoneLabel && (
            <span className="flex items-center gap-1.5">
              <Clock3 className="h-3 w-3" />
              {t('networkPage.groups.timezoneValue', { timezone: tenant.timezone ?? timezoneLabel })}
            </span>
          )}
          {tenant.default_currency && (
            <span className="flex items-center gap-1.5">
              <Coins className="h-3 w-3" />
              {t('networkPage.groups.currencyValue', { currency: tenant.default_currency })}
            </span>
          )}
        </div>
        {commercial && commercial.dataStatus === 'ready' && (
          <div className="mt-2 grid gap-1 text-[11px] text-muted-foreground sm:grid-cols-4">
            <span>{t('networkPage.groups.commercialOpenDeals', { defaultValue: 'Open deals' })}: {commercial.openDeals}</span>
            <span>{t('networkPage.groups.commercialOverdue', { defaultValue: 'Overdue follow-ups' })}: {commercial.overdueFollowUps}</span>
            <span>{t('networkPage.groups.commercialQuotesJobs', { defaultValue: 'Quotes / Jobs' })}: {commercial.openQuotations} / {commercial.activeJobs}</span>
            <span>{formatMoney(commercial.activePipelineValue, commercial.defaultCurrency)}</span>
          </div>
        )}
        {commercial?.dataStatus === 'unavailable' && (
          <div className="mt-2 text-[11px] text-muted-foreground">
            {t('networkPage.groups.commercialUnavailable', { defaultValue: 'RM commercial signals unavailable for this tenant yet.' })}
          </div>
        )}
        {commercial?.topRecommendationCustomerName && (
          <div className="mt-2 text-[11px] text-muted-foreground">
            {t('networkPage.groups.topAccount360Lead', { defaultValue: 'Top account lead' })}: {commercial.topRecommendationCustomerName}
          </div>
        )}
      </div>

      {/* Switch button */}
      <div className="flex shrink-0 flex-col items-end gap-2">
        {commercial && (
          <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-medium capitalize', commercialTone(commercial.healthStatus))}>
            {commercial.healthStatus}
          </span>
        )}
        {!isCurrent && tenant.status === 'active' && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onSwitch}
            disabled={switching}
            className="h-7 gap-1 text-xs shrink-0"
          >
            {t('networkPage.groups.switch')}
            <ArrowRight className="h-3 w-3" />
          </Button>
        )}
        {tenant.status === 'active' && onOpenPipeline && (
          <Button
            size="sm"
            variant="outline"
            onClick={onOpenPipeline}
            disabled={switching}
            className="h-7 gap-1 text-xs shrink-0"
          >
            {t('networkPage.groups.openPipeline', { defaultValue: 'Open pipeline' })}
            <ArrowRight className="h-3 w-3" />
          </Button>
        )}
        {tenant.status === 'active' && onOpenAccount360 && commercial?.topRecommendationCustomerId && (
          <Button
            size="sm"
            variant="outline"
            onClick={onOpenAccount360}
            disabled={switching}
            className="h-7 gap-1 text-xs shrink-0"
          >
            {t('networkPage.groups.openAccount360', { defaultValue: 'Open Account 360' })}
            <ArrowRight className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Group Card ──────────────────────────────────────

type GroupWithTenants = TenantGroup & {
  tenants: GroupTenant[];
  members?: GroupMember[];
  commercialSummary?: GroupCommercialSummaryResponse | null;
  interCompany?: GroupInterCompanyResponse | null;
  audit?: GroupAuditResponse | null;
  financeRollup?: GroupFinanceRollupResponse | null;
};

type GroupScopeTab = 'today' | 'comparison' | 'companies' | 'inter-company' | 'audit' | 'financial';

const SEVERITY_TONE: Record<'info' | 'warning' | 'critical', string> = {
  critical: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  info: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
};

function formatNumber(n: number, currency?: string | null) {
  if (currency) return formatMoney(n, currency);
  return new Intl.NumberFormat('en-SG', { maximumFractionDigits: 0 }).format(n || 0);
}

function GroupCard({ group, currentTenantId, onSwitch, onOpenPipeline, onOpenAccount360, switching }: {
  group: GroupWithTenants;
  currentTenantId: string;
  onSwitch: (tenantId: string) => void;
  onOpenPipeline: (tenantId: string) => void;
  onOpenAccount360: (tenantId: string, customerId: string) => void;
  switching: boolean;
}) {
  const { t } = useTranslation();
  const [scopeTab, setScopeTab] = useState<GroupScopeTab>('today');
  const [commercialFilter, setCommercialFilter] = useState<CommercialFilter>('all');
  const [focusedTenantId, setFocusedTenantId] = useState<string | null>(null);
  const tenantRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const hq = group.tenants.find(t => t.entity_type === 'hq');
  const others = group.tenants.filter(t => t.entity_type !== 'hq');
  const ordered = [...(hq ? [hq] : []), ...others];
  const countries = new Set(group.tenants.map(t => t.country_code).filter(Boolean));
  const commercialByTenantId = new Map((group.commercialSummary?.tenants ?? []).map((tenant) => [tenant.tenantId, tenant]));
  const tenantRows = ordered.map((tenant) => ({
    tenant,
    commercial: commercialByTenantId.get(tenant.id),
  }));
  const attentionRows = tenantRows.filter(({ commercial }) => commercial?.healthStatus === 'attention' || commercial?.dataStatus === 'unavailable');
  const filteredTenantRows = tenantRows.filter(({ commercial }) => matchesCommercialFilter(commercial, commercialFilter));
  const totals = group.commercialSummary?.totals;
  const maxJobsMonthToDate = Math.max(1, ...tenantRows.map(({ commercial }) => commercial?.jobsMonthToDate ?? commercial?.jobsToday ?? 0));
  const maxBilledToday = Math.max(1, ...tenantRows.map(({ commercial }) => commercial?.billedToday ?? 0));
  const maxExceptions = Math.max(1, ...tenantRows.map(({ commercial }) => commercial?.unresolvedExceptions ?? 0));

  const filterMeta: Array<{ key: CommercialFilter; label: string; count: number }> = [
    {
      key: 'all',
      label: t('networkPage.groups.filterAll', { defaultValue: 'All tenants' }),
      count: tenantRows.length,
    },
    {
      key: 'attention',
      label: t('networkPage.groups.filterAttention', { defaultValue: 'Needs attention' }),
      count: tenantRows.filter(({ commercial }) => commercial?.healthStatus === 'attention').length,
    },
    {
      key: 'active',
      label: t('networkPage.groups.filterActive', { defaultValue: 'Commercially active' }),
      count: tenantRows.filter(({ commercial }) => commercial?.healthStatus === 'active').length,
    },
    {
      key: 'unavailable',
      label: t('networkPage.groups.filterUnavailable', { defaultValue: 'Signals unavailable' }),
      count: tenantRows.filter(({ commercial }) => commercial?.dataStatus === 'unavailable').length,
    },
  ];

  const describeCommercialReason = (commercial?: GroupCommercialTenantSummary) => {
    if (!commercial) return t('networkPage.groups.drilldownNoSignals', { defaultValue: 'No commercial signals available for this tenant yet.' });
    if (commercial.dataStatus === 'unavailable') {
      return commercial.dataMessage || t('networkPage.groups.drilldownUnavailable', { defaultValue: 'Commercial data is not ready yet for this tenant.' });
    }

    const reasons: string[] = [];
    if (commercial.overdueFollowUps > 0) reasons.push(`${commercial.overdueFollowUps} overdue follow-ups`);
    if (commercial.staleDeals > 0) reasons.push(`${commercial.staleDeals} stale deals`);
    if (reasons.length === 0 && commercial.openDeals > 0) reasons.push(`${commercial.openDeals} open deals in motion`);
    if (reasons.length === 0 && commercial.activeJobs > 0) reasons.push(`${commercial.activeJobs} active jobs with no sales risk flag`);

    return reasons.join(' · ') || t('networkPage.groups.drilldownSteady', { defaultValue: 'Commercial signals look steady.' });
  };

  const focusTenant = (tenantId: string, filter: CommercialFilter) => {
    setCommercialFilter(filter);
    setFocusedTenantId(tenantId);
  };

  useEffect(() => {
    if (!focusedTenantId) return;
    const target = tenantRefs.current[focusedTenantId];
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusedTenantId, commercialFilter]);

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm">
      {/* Group header */}
      <div className="flex items-center gap-3 border-b px-4 py-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30">
          <Network className="h-4 w-4 text-purple-600 dark:text-purple-300" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold text-foreground">{group.name}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="h-5 px-1.5 text-[10px] uppercase tracking-wide">
              {t('networkPage.groups.exclusiveBadge')}
            </Badge>
            <span>{t('networkPage.groups.summary', { count: group.tenants.length })}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Users className="h-3.5 w-3.5" />
          <span className="capitalize">{group.my_role?.replace('group_', '') ?? t('networkPage.groups.roleFallback')}</span>
        </div>
      </div>

      <Tabs value={scopeTab} onValueChange={(value) => setScopeTab(value as GroupScopeTab)} className="border-b bg-muted/10 px-4 py-3">
        <TabsList className="grid w-full grid-cols-3 md:w-auto md:grid-cols-6">
          <TabsTrigger value="today">{t('networkPage.groups.tabToday', { defaultValue: 'Group Today' })}</TabsTrigger>
          <TabsTrigger value="comparison">{t('networkPage.groups.tabComparison', { defaultValue: 'Comparison' })}</TabsTrigger>
          <TabsTrigger value="companies">{t('networkPage.groups.tabCompanies', { defaultValue: 'Companies' })}</TabsTrigger>
          <TabsTrigger value="inter-company">{t('networkPage.groups.tabInterCompany', { defaultValue: 'Inter-Company' })}</TabsTrigger>
          <TabsTrigger value="audit">{t('networkPage.groups.tabAudit', { defaultValue: 'Audit' })}</TabsTrigger>
          <TabsTrigger value="financial">{t('networkPage.groups.tabFinancial', { defaultValue: 'Financial' })}</TabsTrigger>
        </TabsList>
      </Tabs>

      {scopeTab === 'today' && (
        <>
      <div className="grid gap-3 border-b bg-muted/20 px-4 py-3 text-sm md:grid-cols-3">
        <div className="rounded-lg border bg-background px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{t('networkPage.groups.hqCompany')}</div>
          <div className="mt-1 font-medium text-foreground">{hq?.name ?? t('networkPage.common.unknown')}</div>
        </div>
        <div className="rounded-lg border bg-background px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{t('networkPage.groups.memberCompanies')}</div>
          <div className="mt-1 font-medium text-foreground">{group.tenants.length}</div>
        </div>
        <div className="rounded-lg border bg-background px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{t('networkPage.groups.coverage')}</div>
          <div className="mt-1 font-medium text-foreground">{countries.size}</div>
        </div>
      </div>

      <div className="border-b bg-muted/10 px-4 py-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-foreground">
              {t('networkPage.groups.commercialTitle', { defaultValue: 'Commercial Overview' })}
            </div>
            <div className="text-xs text-muted-foreground">
              {t('networkPage.groups.commercialSubtitle', { defaultValue: 'Cross-tenant RM heat map for this ownership group.' })}
            </div>
          </div>
          {group.commercialSummary?.generatedAt && (
            <div className="text-[11px] text-muted-foreground">
              {t('networkPage.groups.commercialUpdated', { defaultValue: 'Updated {{date}}', date: formatDate(group.commercialSummary.generatedAt) })}
            </div>
          )}
        </div>
        {group.commercialSummary ? (
          <div className="grid gap-3 md:grid-cols-4">
            <MetricCard icon={Users} label={t('networkPage.groups.todayUsers', { defaultValue: 'Active users' })} value={`${totals?.activeUsers ?? 0} / ${totals?.totalUsers ?? 0}`} />
            <MetricCard icon={BriefcaseBusiness} label={t('networkPage.groups.jobsTodayMonthToDate', { defaultValue: 'Jobs today / MTD' })} value={`${totals?.jobsToday ?? 0} / ${totals?.jobsMonthToDate ?? 0}`} />
            <MetricCard icon={Coins} label={t('networkPage.groups.todayBilled', { defaultValue: 'Billed today' })} value={formatMoney(totals?.billedToday ?? 0)} />
            <MetricCard icon={AlertTriangle} label={t('networkPage.groups.todayExceptions', { defaultValue: 'Open exceptions' })} value={`${totals?.unresolvedExceptions ?? 0}`} />
          </div>
        ) : (
          <div className="rounded-lg border border-dashed bg-background px-3 py-4 text-sm text-muted-foreground">
            {t('networkPage.groups.commercialLoadFailed', { defaultValue: 'Commercial overview is temporarily unavailable.' })}
          </div>
        )}

        {group.commercialSummary && attentionRows.length > 0 && (
          <div className="mt-3 rounded-lg border bg-background">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="attention-queue" className="border-none">
                <AccordionTrigger className="px-3 py-3 hover:no-underline">
                  <div className="flex items-center gap-2 text-left">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        {t('networkPage.groups.attentionQueueTitle', { defaultValue: 'Attention queue' })}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t('networkPage.groups.attentionQueueSubtitle', { defaultValue: 'Which subsidiaries need HQ follow-up right now.' })}
                      </div>
                    </div>
                    <Badge className="ml-1 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                      {attentionRows.length}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3">
                  <div className="space-y-2">
                    {attentionRows.map(({ tenant, commercial }) => (
                      <div key={tenant.id} className="rounded-lg border bg-muted/20 px-3 py-2">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-foreground">{tenant.name}</div>
                            <div className="mt-0.5 text-xs text-muted-foreground">{describeCommercialReason(commercial)}</div>
                          </div>
                          <Badge className={cn('shrink-0 capitalize', commercialTone(commercial?.healthStatus))}>
                            {commercial?.dataStatus === 'unavailable'
                              ? t('networkPage.groups.filterUnavailable', { defaultValue: 'Signals unavailable' })
                              : commercial?.healthStatus}
                          </Badge>
                        </div>
                        {commercial?.dataStatus === 'ready' && (
                          <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-muted-foreground">
                            <span>{t('networkPage.groups.commercialOpenDeals', { defaultValue: 'Open deals' })}: {commercial.openDeals}</span>
                            <span>{t('networkPage.groups.commercialOverdue', { defaultValue: 'Overdue follow-ups' })}: {commercial.overdueFollowUps}</span>
                            <span>{t('networkPage.groups.drilldownNextFollowUp', { defaultValue: 'Next follow-up' })}: {formatDate(commercial.nextFollowUpAt)}</span>
                            <span>{t('networkPage.groups.drilldownLastActivity', { defaultValue: 'Last activity' })}: {formatDate(commercial.lastActivityAt)}</span>
                            {commercial.topRecommendationCustomerName && (
                              <span>{t('networkPage.groups.topAccount360Lead', { defaultValue: 'Top account lead' })}: {commercial.topRecommendationCustomerName}</span>
                            )}
                          </div>
                        )}
                        <div className="mt-2 flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 text-[11px]"
                            onClick={() => focusTenant(
                              tenant.id,
                              commercial?.dataStatus === 'unavailable'
                                ? 'unavailable'
                                : commercial?.healthStatus === 'attention'
                                  ? 'attention'
                                  : 'all',
                            )}
                          >
                            {t('networkPage.groups.focusTenant', { defaultValue: 'Focus subsidiary' })}
                          </Button>
                          {tenant.status === 'active' && (
                            <Button
                              type="button"
                              size="sm"
                              className="h-7 text-[11px]"
                              onClick={() => onOpenPipeline(tenant.id)}
                              disabled={switching}
                            >
                              {currentTenantId === tenant.id
                                ? t('networkPage.groups.openPipeline', { defaultValue: 'Open pipeline' })
                                : t('networkPage.groups.switchAndOpenPipeline', { defaultValue: 'Switch & open pipeline' })}
                            </Button>
                          )}
                          {tenant.status === 'active' && commercial?.topRecommendationCustomerId && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-7 text-[11px]"
                              onClick={() => onOpenAccount360(tenant.id, commercial.topRecommendationCustomerId!)}
                              disabled={switching}
                            >
                              {currentTenantId === tenant.id
                                ? t('networkPage.groups.openAccount360', { defaultValue: 'Open Account 360' })
                                : t('networkPage.groups.switchAndOpenAccount360', { defaultValue: 'Switch & open Account 360' })}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        )}

        {group.commercialSummary && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="mr-1 flex items-center gap-1 text-xs text-muted-foreground">
              <ListFilter className="h-3.5 w-3.5" />
              {t('networkPage.groups.filterTitle', { defaultValue: 'Show' })}
            </div>
            {filterMeta.map((filter) => (
              <Button
                key={filter.key}
                type="button"
                size="sm"
                variant={commercialFilter === filter.key ? 'default' : 'outline'}
                className="h-7 gap-1 rounded-full px-2.5 text-[11px]"
                onClick={() => setCommercialFilter(filter.key)}
              >
                {filter.key === 'attention' && <AlertTriangle className="h-3 w-3" />}
                {filter.key === 'active' && <Activity className="h-3 w-3" />}
                {filter.key === 'unavailable' && <CircleOff className="h-3 w-3" />}
                {filter.label}
                <span className="rounded-full bg-black/10 px-1.5 py-0 text-[10px] dark:bg-white/10">{filter.count}</span>
              </Button>
            ))}
          </div>
        )}
      </div>
        </>
      )}

      {scopeTab === 'comparison' && (
        <div className="border-b bg-muted/10 px-4 py-4">
          <div className="mb-3 flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-primary" />
            <div>
              <div className="text-sm font-semibold text-foreground">{t('networkPage.groups.comparisonTitle', { defaultValue: 'Company comparison' })}</div>
              <div className="text-xs text-muted-foreground">{t('networkPage.groups.comparisonSubtitle', { defaultValue: 'Jobs today and month-to-date, billing, and unresolved exceptions across group companies.' })}</div>
            </div>
          </div>
          <div className="space-y-3">
            {tenantRows.map(({ tenant, commercial }) => (
              <div key={tenant.id} className="rounded-lg border bg-background p-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="min-w-0 truncate text-sm font-medium text-foreground">{tenant.name}</div>
                  <Badge variant="outline" className={cn('capitalize', commercialTone(commercial?.healthStatus))}>
                    {commercial?.dataStatus === 'unavailable'
                      ? t('networkPage.groups.filterUnavailable', { defaultValue: 'Signals unavailable' })
                      : commercial?.healthStatus ?? tenant.status}
                  </Badge>
                </div>
                <div className="space-y-2 text-xs">
                  <div className="grid grid-cols-[96px_1fr_92px] items-center gap-2">
                    <span className="text-muted-foreground">{t('networkPage.groups.jobsTodayMonthToDateShort', { defaultValue: 'Jobs T/MTD' })}</span>
                    <div className="h-2 rounded-full bg-muted">
                      <div className="h-2 rounded-full bg-primary" style={{ width: `${Math.max(4, ((commercial?.jobsMonthToDate ?? commercial?.jobsToday ?? 0) / maxJobsMonthToDate) * 100)}%` }} />
                    </div>
                    <span className="text-right tabular-nums">{commercial?.jobsToday ?? 0} / {commercial?.jobsMonthToDate ?? 0}</span>
                  </div>
                  <div className="grid grid-cols-[96px_1fr_76px] items-center gap-2">
                    <span className="text-muted-foreground">{t('networkPage.groups.todayBilled', { defaultValue: 'Billed today' })}</span>
                    <div className="h-2 rounded-full bg-muted"><div className="h-2 rounded-full bg-emerald-500" style={{ width: `${Math.max(4, ((commercial?.billedToday ?? 0) / maxBilledToday) * 100)}%` }} /></div>
                    <span className="text-right tabular-nums">{formatMoney(commercial?.billedToday ?? 0, commercial?.defaultCurrency)}</span>
                  </div>
                  <div className="grid grid-cols-[96px_1fr_76px] items-center gap-2">
                    <span className="text-muted-foreground">{t('networkPage.groups.todayExceptions', { defaultValue: 'Open exceptions' })}</span>
                    <div className="h-2 rounded-full bg-muted"><div className="h-2 rounded-full bg-amber-500" style={{ width: `${Math.max(4, ((commercial?.unresolvedExceptions ?? 0) / maxExceptions) * 100)}%` }} /></div>
                    <span className="text-right tabular-nums">{commercial?.unresolvedExceptions ?? 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {scopeTab === 'inter-company' && (
        <div className="border-b bg-muted/10 px-4 py-4 space-y-3">
          <div className="flex items-center gap-2">
            <Network className="h-4 w-4 text-primary" />
            <div>
              <div className="text-sm font-semibold text-foreground">Inter-Company Flows</div>
              <div className="text-xs text-muted-foreground">Jobs tagged with `inter_company_partner_tenant_id` across the group.</div>
            </div>
          </div>
          {!group.interCompany ? (
            <div className="rounded-lg border border-dashed bg-background px-3 py-4 text-sm text-muted-foreground">No inter-company data yet.</div>
          ) : (
            <>
              <div className="grid gap-3 md:grid-cols-3">
                <MetricCard icon={Network} label="Total inter-co jobs" value={`${group.interCompany.totals.interCompanyJobs}`} />
                <MetricCard icon={ArrowRight} label="Tenant pairs" value={`${group.interCompany.totals.pairsWithFlow}`} />
                <MetricCard icon={AlertTriangle} label="Missing charge code" value={`${group.interCompany.totals.missingChargeCode}`} />
              </div>
              {group.interCompany.flows.length === 0 ? (
                <div className="rounded-lg border border-dashed bg-background px-3 py-4 text-sm text-muted-foreground">No flows detected — sample data may not yet tag inter-company partners.</div>
              ) : (
                <div className="overflow-hidden rounded-lg border bg-background">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left">From</th>
                        <th className="px-3 py-2 text-left">→ To</th>
                        <th className="px-3 py-2 text-right">Jobs</th>
                        <th className="px-3 py-2 text-right">With code</th>
                        <th className="px-3 py-2 text-right">Missing</th>
                        <th className="px-3 py-2 text-left">Top charge codes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.interCompany.flows.map((flow) => (
                        <tr key={`${flow.fromTenantId}-${flow.toTenantId}`} className="border-t">
                          <td className="px-3 py-2 truncate font-medium">{flow.fromTenantName}</td>
                          <td className="px-3 py-2 truncate">{flow.toTenantName}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{flow.jobCount}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-emerald-600">{flow.withChargeCode}</td>
                          <td className={cn('px-3 py-2 text-right tabular-nums', flow.missingChargeCode > 0 && 'text-amber-600')}>{flow.missingChargeCode}</td>
                          <td className="px-3 py-2 text-[11px] text-muted-foreground">
                            {flow.topChargeCodes.length === 0
                              ? '—'
                              : flow.topChargeCodes.map((c) => `${c.code}(${c.count})`).join(', ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {group.interCompany.tenantSummaries.length > 0 && (
                <div className="overflow-hidden rounded-lg border bg-background">
                  <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-muted-foreground">Per-tenant outbound vs inbound</div>
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left">Tenant</th>
                        <th className="px-3 py-2 text-right">Outbound</th>
                        <th className="px-3 py-2 text-right">Inbound</th>
                        <th className="px-3 py-2 text-right">Reciprocity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.interCompany.tenantSummaries.map((row) => (
                        <tr key={row.tenantId} className="border-t">
                          <td className="px-3 py-2 truncate">{row.name}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{row.outboundJobs}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{row.inboundJobs}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{row.reciprocityRatio.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {scopeTab === 'audit' && (
        <div className="border-b bg-muted/10 px-4 py-4 space-y-3">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            <div>
              <div className="text-sm font-semibold text-foreground">Audit findings</div>
              <div className="text-xs text-muted-foreground">Group-level rules: inter-co charge codes · monthly volume drop · AR overdue 60d.</div>
            </div>
          </div>
          {!group.audit ? (
            <div className="rounded-lg border border-dashed bg-background px-3 py-4 text-sm text-muted-foreground">No audit data yet.</div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                <Badge className={SEVERITY_TONE.critical}>Critical: {group.audit.totals.critical}</Badge>
                <Badge className={SEVERITY_TONE.warning}>Warning: {group.audit.totals.warning}</Badge>
                <Badge className={SEVERITY_TONE.info}>Info: {group.audit.totals.info}</Badge>
              </div>
              {group.audit.findings.length === 0 ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-4 text-sm text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">All clear — no audit findings.</div>
              ) : (
                <div className="space-y-2">
                  {group.audit.findings.map((f: GroupAuditFinding) => (
                    <div key={f.id} className="rounded-lg border bg-background p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-foreground">{f.title}</div>
                          <div className="mt-0.5 text-xs text-muted-foreground">{f.tenantName} · <span className="font-mono">{f.rule}</span></div>
                        </div>
                        <Badge className={cn('shrink-0 capitalize', SEVERITY_TONE[f.severity])}>{f.severity}</Badge>
                      </div>
                      <div className="mt-2 text-xs text-muted-foreground">{f.detail}</div>
                      {f.metric && (
                        <div className="mt-1 text-[11px] text-muted-foreground">
                          {f.metric.label}: <span className="font-medium tabular-nums text-foreground">{f.metric.value}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {scopeTab === 'financial' && (
        <div className="border-b bg-muted/10 px-4 py-4 space-y-3">
          <div className="flex items-center gap-2">
            <Coins className="h-4 w-4 text-primary" />
            <div>
              <div className="text-sm font-semibold text-foreground">Financial rollup</div>
              <div className="text-xs text-muted-foreground">AR posted, AR open/overdue, AP open per tenant in native currency. No FX conversion.</div>
            </div>
          </div>
          {!group.financeRollup ? (
            <div className="rounded-lg border border-dashed bg-background px-3 py-4 text-sm text-muted-foreground">No financial data yet.</div>
          ) : (
            <>
              {group.financeRollup.currencyBreakdown.length > 0 && (
                <div className="grid gap-3 md:grid-cols-3">
                  {group.financeRollup.currencyBreakdown.map((c) => (
                    <div key={c.currency} className="rounded-lg border bg-background px-3 py-2">
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{c.currency} · {c.tenantCount} tenants</div>
                      <div className="mt-1 text-sm">
                        <div>AR posted: <span className="font-medium tabular-nums">{formatNumber(c.arPosted, c.currency)}</span></div>
                        <div>AR open: <span className="font-medium tabular-nums">{formatNumber(c.arOpen, c.currency)}</span></div>
                        <div className="text-amber-700">AR &gt;60d: <span className="font-medium tabular-nums">{formatNumber(c.arOverdue60, c.currency)}</span></div>
                        <div>AP open: <span className="font-medium tabular-nums">{formatNumber(c.apOpen, c.currency)}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="overflow-hidden rounded-lg border bg-background">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left">Tenant</th>
                      <th className="px-3 py-2 text-left">Cur</th>
                      <th className="px-3 py-2 text-right">AR posted</th>
                      <th className="px-3 py-2 text-right">AR open</th>
                      <th className="px-3 py-2 text-right">AR &gt;60d</th>
                      <th className="px-3 py-2 text-right">AR &gt;90d</th>
                      <th className="px-3 py-2 text-right">AP open</th>
                      <th className="px-3 py-2 text-right">Jobs MTD/LM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.financeRollup.tenants.map((row) => (
                      <tr key={row.tenantId} className={cn('border-t', row.dataStatus !== 'ready' && 'opacity-50')}>
                        <td className="px-3 py-2 truncate font-medium">{row.name}</td>
                        <td className="px-3 py-2">{row.currency}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatNumber(row.arPosted, row.currency)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatNumber(row.arOpen, row.currency)}</td>
                        <td className={cn('px-3 py-2 text-right tabular-nums', row.arOverdue60 > 0 && 'text-amber-600')}>{formatNumber(row.arOverdue60, row.currency)}</td>
                        <td className={cn('px-3 py-2 text-right tabular-nums', row.arOverdue90 > 0 && 'text-rose-600')}>{formatNumber(row.arOverdue90, row.currency)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatNumber(row.apOpen, row.currency)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.jobsThisMonth} / {row.jobsLastMonth}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Tenants list */}
      <div className={cn('space-y-2 p-4', scopeTab !== 'companies' && scopeTab !== 'today' && 'hidden')}>
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
          <Building2 className="h-4 w-4 text-primary" />
          {t('networkPage.groups.companyMembersTitle')}
        </div>
        {filteredTenantRows.length === 0 ? (
          <div className="rounded-lg border border-dashed px-3 py-5 text-sm text-muted-foreground">
            {t('networkPage.groups.filterEmpty', { defaultValue: 'No subsidiaries match this commercial filter.' })}
          </div>
        ) : (
          filteredTenantRows.map(({ tenant, commercial }) => (
            <div
              key={tenant.id}
              ref={(node) => {
                tenantRefs.current[tenant.id] = node;
              }}
            >
              <SubsidiaryCard
                tenant={tenant}
                commercial={commercial}
                isCurrent={tenant.id === currentTenantId}
                isFocused={tenant.id === focusedTenantId}
                switching={switching}
                onSwitch={() => onSwitch(tenant.id)}
                onOpenPipeline={() => onOpenPipeline(tenant.id)}
                onOpenAccount360={commercial?.topRecommendationCustomerId ? () => onOpenAccount360(tenant.id, commercial.topRecommendationCustomerId!) : undefined}
              />
            </div>
          ))
        )}

        {(group.members?.length ?? 0) > 0 && (
          <div className="mt-5 rounded-lg border border-dashed p-3">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
              <Shield className="h-4 w-4 text-primary" />
              {t('networkPage.groups.adminMembersTitle', { count: group.members?.length ?? 0 })}
            </div>
            <div className="space-y-2">
              {group.members?.map(member => (
                <div key={member.id} className="flex items-center justify-between gap-3 rounded-md border bg-background px-3 py-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">{member.name || member.email}</div>
                    <div className="truncate text-[11px] text-muted-foreground">{member.email}</div>
                  </div>
                  <Badge variant="outline" className="shrink-0 text-[10px] capitalize">
                    {member.role.replace('group_', '')}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────

export default function GroupPage() {
  const { t } = useTranslation();
  const { user, switchGroupTenant } = useAuth();
  const [groups, setGroups] = useState<GroupWithTenants[]>([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  const load = () => {
    setLoading(true);
    groupApi.list()
      .then(async (list) => {
        const detailed = await Promise.all(list.map(async (g) => {
          const [detail, commercialSummary, interCompany, audit, financeRollup] = await Promise.all([
            groupApi.get(g.id).catch(() => ({ ...g, tenants: [] })),
            groupApi.getCommercialSummary(g.id).catch(() => null),
            groupApi.getInterCompanyFlows(g.id).catch(() => null),
            groupApi.getAuditFindings(g.id).catch(() => null),
            groupApi.getFinanceRollup(g.id).catch(() => null),
          ]);
          return {
            ...(detail as TenantGroup & { tenants: GroupTenant[]; members?: GroupMember[] }),
            commercialSummary,
            interCompany,
            audit,
            financeRollup,
          };
        }));
        setGroups(detailed as GroupWithTenants[]);
      })
      .catch(() => toast.error(t('networkPage.groups.loadFailed')))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSwitch = async (groupId: string, tenantId: string, redirectPath?: string) => {
    setSwitching(true);
    try {
      await switchGroupTenant(groupId, tenantId);
      toast.success(t('networkPage.groups.switchSuccess'));
      if (redirectPath) {
        setTimeout(() => window.location.assign(redirectPath), 250);
      } else {
        setTimeout(() => window.location.reload(), 800);
      }
    } catch {
      toast.error(t('networkPage.groups.switchFailed'));
    } finally {
      setSwitching(false);
    }
  };

  const handleOpenPipeline = async (groupId: string, tenantId: string) => {
    if (tenantId === user?.tenantId) {
      window.location.assign('/oms/pipeline#deal-health-radar');
      return;
    }
    await handleSwitch(groupId, tenantId, '/oms/pipeline#deal-health-radar');
  };

  const handleOpenAccount360 = async (groupId: string, tenantId: string, customerId: string) => {
    const targetPath = `/e/customers/${customerId}?tab=crm360`;
    if (tenantId === user?.tenantId) {
      window.location.assign(targetPath);
      return;
    }
    await handleSwitch(groupId, tenantId, targetPath);
  };

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6 p-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">{t('networkPage.groups.title')}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t('networkPage.groups.subtitle')}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1.5">
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          {t('networkPage.common.refresh')}
        </Button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2].map(i => (
            <Skeleton key={i} className="h-48 w-full rounded-xl" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed py-16 text-center">
          <Network className="h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm font-medium text-muted-foreground">{t('networkPage.groups.emptyTitle')}</p>
          <p className="text-xs text-muted-foreground/70">
            {t('networkPage.groups.emptyDesc')}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(g => (
            <GroupCard
              key={g.id}
              group={g}
              currentTenantId={user?.tenantId ?? ''}
              switching={switching}
              onSwitch={(tid) => handleSwitch(g.id, tid)}
              onOpenPipeline={(tid) => handleOpenPipeline(g.id, tid)}
              onOpenAccount360={(tenantId, customerId) => handleOpenAccount360(g.id, tenantId, customerId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
